import React, { useRef } from 'react';
import { useNavigate } from 'react-router-dom';

import { updateSingleInvoice } from 'api/Invoice';

import { cn } from 'lib/helpers/utility';
import { appointmentKeys } from 'lib/queries/appointmentKeys';
import { useCustomMutation } from 'lib/hooks/useCustomMutation';
import { overviewLedgerQueryKeys } from 'lib/constants/queries/queryKeys';
import { useInvoiceEditContext } from 'lib/context/InvoiceContext/InvoiceContext';

import Button from 'components/shared/Button/Button';

import EditInvoiceModal from '../../EditInvoiceModal/EditInvoiceModal';

const InvoiceEditButton = ({ preview, openModal, invoiceId, variant }) => {
  const navigate = useNavigate();
  const { editedInvoice, updateInvoice } = useInvoiceEditContext();
  const editModalRef = useRef(null);

  const { mutateAsync } = useCustomMutation({
    mutationFn: (finalInvoice) => updateSingleInvoice(navigate, { invoice: finalInvoice }),
    successMessage: 'Invoice updated',
    errorMessage: "Couldn't update invoice",
    invalidateQueryKey: [
      ['internal_invoice', invoiceId],
      ['internal_invoices'],
      appointmentKeys.invoicesAll(),
      ['patientInvoices'],
      ...overviewLedgerQueryKeys
    ],
    onSuccess: (response) => {
      const updatedInvoice = response?.invoice || editedInvoice;
      updateInvoice(updatedInvoice);
    },
    handleResponse: (res) => {
      if (res?.code !== 0) throw Error(res?.error);
    }
  });

  const handleSaveChanges = () => {
    const finalInvoice = editModalRef.current?.prepareInvoiceForSave?.();
    if (!finalInvoice) {
      return Promise.reject(new Error());
    }
    return mutateAsync(finalInvoice);
  };

  if (preview) return null;

  const isPrimary = variant === 'primary';

  return (
    <Button
      variant="link"
      size="small"
      icon="edit-3-mono"
      iconSize={16}
      iconColor="neutral"
      className={cn('h-6 w-6 hover:bg-neutral-200', isPrimary && 'hover:bg-primary-100')}
      onClick={() => {
        openModal({
          title: `Invoice #${invoiceId}`,
          submitText: 'Save changes',
          onSubmit: handleSaveChanges,
          content: (props) => (
            <EditInvoiceModal ref={editModalRef} invoiceId={invoiceId} {...props} />
          )
        });
      }}
    />
  );
};

export default InvoiceEditButton;
